import { makeAutoObservable } from 'mobx';
import { z } from 'zod';
import {
  genId,
  INVALID_VERSION_MESSAGE,
  RequireAllProps,
  type StringDto,
  validateVersion,
} from '../../../../../../store/utils/Dto';
import extractValue, {
  type EventOrValue,
} from '../../../../../../store/utils/extractValue';
import Validator from '../../../../../../store/utils/Validator';
import { errorHandler } from '../../../../../../utils/errors';
import type { RuleDto } from '../../fileMovements/store/Rule';

export default class BeforeInstall {
  static readonly Schema = z.object({
    message: z.string().min(1),
    okText: z.string(),
    title: z.string(),
    version: z.string().min(1).refine(validateVersion, INVALID_VERSION_MESSAGE),
  });

  readonly id = genId();
  message = '';
  okText = '';
  title = '';
  version = '*';

  private readonly validator = new Validator(this, BeforeInstall.Schema);

  constructor(
    dto: BeforeInstallDto[] | undefined,
    private readonly onSave: (beforeInstall: BeforeInstall) => Promise<void>,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    // Currently, only one item is implemented. Could be changed later
    const [item = {} as BeforeInstallDto] = dto || [];
    this.message = item.message || '';
    this.okText = item.okText || '';
    this.title = item.title || '';
    this.version = item.version || '*';
  }

  get errors(): StringDto<RuleDto> {
    return this.isEmpty ? {} : this.validator.errorsForEnabledFields;
  }

  get isEmpty() {
    return !this.hasMessage && ['', '*'].includes(this.version);
  }

  get hasMessage() {
    return Boolean(this.message.replaceAll(/<[^>]*>|&nbsp;|\s/g, ''));
  }

  handleChange(e: EventOrValue) {
    const { name, value } = extractValue(e);
    Object.assign(this, { [name]: value });
    this.validator.validate();
    this.save();
  }

  handleBlur(e: EventOrValue<keyof BeforeInstallDto>) {
    const { name } = extractValue(e);
    this.validator.enableFieldValidation(name);
  }

  save() {
    this.onSave(this).catch(errorHandler('Cannot save rule'));
  }

  toJSON(): Array<RequireAllProps<BeforeInstallDto>> {
    if (!this.hasMessage || !this.version) {
      return [];
    }

    return [
      {
        message: this.message || '',
        okText: this.okText || undefined,
        title: this.title || undefined,
        version: this.version || '*',
      },
    ];
  }
}

export interface BeforeInstallDto {
  message: string;
  okText?: string;
  title?: string;
  version: string;
}
